import type { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS, type Block } from '@contentful/rich-text-types'
import classNames from 'classnames'
import React from 'react'

import type { Links } from '@ecomm/contentful-schemas'
import { ApolloRichText } from '@ecomm/contentful/components'
import { ColoredSection } from '.'
import ColoredSectionEmbeddedEntry from './ColoredSectionEmbeddedEntry'
import { type GatsbyColoredSectionSchema, themes } from './schema'
import { useColoredSectionQuery } from './useColoredSectionQuery'

const renderOptions = (links: Links): Options['renderNode'] => {
  const entries = new Map()

  links?.entries?.block?.forEach(link => link && entries.set(link.id, link))

  return {
    [BLOCKS.EMBEDDED_ENTRY]: node => {
      const {
        data: {
          target: {
            sys: { id: targetId },
            content
          }
        }
      } = node
      const entry = entries.get(targetId)
      const newEntry: Block = {
        data: {
          target: entry
        },
        nodeType: BLOCKS.EMBEDDED_ENTRY,
        content: content
      }
      return <ColoredSectionEmbeddedEntry node={newEntry} />
    }
  }
}

export function ApolloColoredSection({
  contentful_id
}: GatsbyColoredSectionSchema) {
  const { headline, contentCollection, disclaimer, theme } =
    useColoredSectionQuery(contentful_id)
  const content = contentCollection.items
  return (
    <div
      className={classNames(
        'rounded-base h-auto w-full px-4 py-6 md:p-8 lg:p-16',
        {
          'bg-neutral-light-50':
            theme === themes.grayWithOrange || themes.lightGrayWithNavyText,
          'bg-neutral-black':
            theme === themes.navyWithWhiteText ||
            theme === themes.navyWithOrange,
          'bg-primary-100': theme === themes.orangeWithNavyText
        }
      )}
      data-component={ColoredSection.name}
      data-testid={ColoredSection.name}
    >
      <h2
        className={classNames('w-full text-center', {
          'text-white':
            theme === themes.navyWithWhiteText ||
            theme === themes.navyWithOrange,
          'mb-6':
            theme === themes.orangeWithNavyText ||
            theme === themes.navyWithWhiteText ||
            theme === themes.lightGrayWithNavyText
        })}
      >
        {headline}
      </h2>
      <div
        className={classNames('grid grid-cols-1 gap-4 md:gap-8 lg:gap-16', {
          'md:grid-cols-1': content.length <= 1,
          'md:grid-cols-2': content.length > 1
        })}
      >
        {content.map((smallText, index) => (
          <div
            className={classNames(
              'rounded-base prose-h3:mb-sm prose-h3:text-center prose-h3:text-body-size prose-h3:font-bold col-span-1',
              {
                //It only will get yellow background when theme is gray with orange and it's the second column or when the theme is navy with orange.
                'bg-primary-100':
                  (theme === themes.grayWithOrange && index === 1) ||
                  theme === themes.navyWithOrange,
                //The second column currently shows first in mobile devices (only with gray-with-orange theme)
                'order-1 md:order-2':
                  theme === themes.grayWithOrange && index === 1,
                'order-2 md:order-1':
                  theme === themes.grayWithOrange && index !== 1,
                'prose-p:text-white prose-a:text-white':
                  theme === themes.navyWithWhiteText,
                'p-4 md:p-8':
                  theme !== themes.navyWithWhiteText &&
                  theme !== themes.orangeWithNavyText &&
                  theme !== themes.lightGrayWithNavyText,
                'text-center':
                  theme === themes.navyWithWhiteText ||
                  theme === themes.orangeWithNavyText ||
                  theme === themes.lightGrayWithNavyText
              }
            )}
            data-testid="ColoredColumn"
            key={index}
          >
            <ApolloRichText
              json={smallText.text.json}
              links={smallText.text.links}
              options={renderOptions}
            />
          </div>
        ))}
      </div>
      {disclaimer?.json ? (
        <div
          className={classNames(
            'prose-p:whitespace-pre-line prose-p:text-xs mt-4 w-full px-8 text-center md:mt-12',
            {
              'prose-p:text-white':
                theme === themes.navyWithWhiteText ||
                theme === themes.navyWithOrange
            }
          )}
        >
          <ApolloRichText json={disclaimer.json} />
        </div>
      ) : null}
    </div>
  )
}
